/**
 * Conversation / decision list pane (2nd column). Ported from the storybook
 * `final/_helpdesk.tsx` `ConvList` + `ConvRow` layout — header with the view
 * title and a count badge, then a scrolling list of rows. Rows show only what
 * the backend carries: avatar + contact name + subject/reason + channel +
 * session-status pill + assignee. All fiction (priority, SLA, tickets, unread)
 * is dropped per the inbox backend-gaps doc.
 */
import { cn } from "@cometchat-ai/ui-dashboard";

import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { FadeIn } from "../components/motion";
import { formatApiError } from "../lib/format-error";
import type { InboxDecision, LiveSession } from "../handoff/api";
import type { ViewId } from "./InboxViewsRail";
import { ChannelPill, HdAvatar, StatusPill, contactName, fmtTime } from "./helpers";

function SessionRow({
  session,
  selected,
  onSelect,
}: {
  session: LiveSession;
  selected: boolean;
  onSelect: () => void;
}) {
  const name = contactName(session.contact, session.title);
  // The title is now the primary label for anonymous contacts, so the subline
  // shows the conversation's last message (what's happening), then the handoff
  // reason (why it's here) when there's no message yet.
  const subline =
    session.lastMessagePreview ??
    session.reason ??
    (name === session.title ? null : (session.title ?? session.channel));
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      data-testid={`helpdesk-row-${session.id}`}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
        selected
          ? "bg-muted"
          : "hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)]",
      )}
    >
      <HdAvatar name={name} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-2sm font-medium text-foreground">
            {name}
          </span>
          <StatusPill status={session.status} />
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="min-w-0 flex-1 truncate">{subline ?? ""}</span>
          <span className="ml-auto shrink-0 text-[color:var(--color-fg-subtle)]">
            {fmtTime(session.lastActivityAt)}
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 overflow-hidden">
          <ChannelPill channel={session.channel} />
          {session.teamName ? (
            <span
              className="inline-flex max-w-28 shrink-0 items-center truncate rounded-full bg-muted px-1.5 py-0.5 text-xxs text-muted-foreground"
              title={session.teamName}
              data-testid="helpdesk-team-chip"
            >
              {session.teamName}
            </span>
          ) : null}
          {session.claimedBy?.displayName ? (
            <span className="ml-auto flex min-w-0 items-center gap-1 truncate text-xxs text-[color:var(--color-fg-subtle)]">
              {session.claimedBy.displayName}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function DecisionRow({
  decision,
  selected,
  onSelect,
}: {
  decision: InboxDecision;
  selected: boolean;
  onSelect: () => void;
}) {
  const name = contactName(decision.contact, decision.subjectKey);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      data-testid={`helpdesk-row-${decision.id}`}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
        selected
          ? "bg-muted"
          : "hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)]",
      )}
    >
      <HdAvatar name={name} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-2sm font-medium text-foreground">
            {name}
          </span>
          <span className="shrink-0 text-xxs text-[color:var(--color-fg-subtle)]">
            {fmtTime(decision.createdAt)}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{decision.questionText}</p>
      </div>
    </button>
  );
}

export interface ListPill {
  id: string;
  label: string;
  count: number;
}

/** Compact display for large counts so a busy desk's numbers stay readable:
 *  999 → "999", 1 234 → "1.2k", 45 600 → "45k", 2 300 000 → "2.3m". */
function compactCount(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 10_000) return `${Math.floor(n / 100) / 10}k`;
  if (n < 1_000_000) return `${Math.floor(n / 1_000)}k`;
  return `${Math.floor(n / 100_000) / 10}m`;
}

export function ConvList({
  title,
  view,
  sessions,
  decisions,
  selectedId,
  onSelect,
  isPending,
  isError,
  error,
  onRetry,
  emptyLabel,
  pills,
  activePill,
  onPillSelect,
}: {
  title: string;
  view: ViewId;
  sessions: LiveSession[];
  decisions: InboxDecision[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  emptyLabel: string;
  /** Optional sub-filter pills under the header (e.g. My chats → All / New). */
  pills?: ReadonlyArray<ListPill>;
  activePill?: string;
  onPillSelect?: (id: string) => void;
}) {
  const count = view === "decisions" ? decisions.length : sessions.length;
  const isEmpty = count === 0;

  return (
    <div className="flex min-w-72 max-w-96 flex-1 flex-col border-r border-border">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4.5">
        <span className="truncate text-base font-semibold tracking-[0.01em] text-foreground">
          {title}
        </span>
        <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
          {compactCount(count)}
        </span>
      </div>
      {pills && pills.length > 0 ? (
        <div className="flex shrink-0 items-center gap-1 border-b border-border px-3 py-2">
          {pills.map((p) => {
            const active = p.id === activePill;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onPillSelect?.(p.id)}
                data-testid={`helpdesk-pill-${p.id}`}
                aria-pressed={active}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                  active
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] hover:text-foreground",
                )}
              >
                {p.label}
                <span
                  className={cn(
                    "tabular-nums",
                    active ? "text-muted-foreground" : "text-[color:var(--color-fg-subtle)]",
                  )}
                >
                  {/* tiny chips: cap rather than abbreviate */}
                  {p.count > 99 ? "99+" : p.count}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 space-y-1 overflow-auto px-1.5 py-2">
        {isPending ? (
          <ListSkeleton rows={6} itemClassName="h-16 w-full" className="space-y-2 px-1.5" />
        ) : isError ? (
          <div className="px-2 py-6">
            <ErrorState message={formatApiError(error)} onRetry={onRetry} />
          </div>
        ) : isEmpty ? (
          <div className="px-4.5 py-10 text-center text-2sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : view === "decisions" ? (
          decisions.map((d, i) => (
            <FadeIn key={d.id} index={i}>
              <DecisionRow
                decision={d}
                selected={d.id === selectedId}
                onSelect={() => onSelect(d.id)}
              />
            </FadeIn>
          ))
        ) : (
          sessions.map((s, i) => (
            <FadeIn key={s.id} index={i}>
              <SessionRow
                session={s}
                selected={s.id === selectedId}
                onSelect={() => onSelect(s.id)}
              />
            </FadeIn>
          ))
        )}
      </div>
    </div>
  );
}
